import { type FunctionComponent } from 'react';
import {
    getSrcString,
    getSrcSetString,
    RESP_IMAGE_DEFAULT_SIZE,
    MOBILE_IMAGE_DEFAULT_SIZE,
} from '../../utils/srcStringHelpers.ts';
import classnames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { type DesignsCarousel_iconicDesignsDirectoryPage$data as IconicDesignsDirectoryPage } from './__generated__/DesignsCarousel_iconicDesignsDirectoryPage.graphql.ts';

// components
import { Carousel } from 'dibs-carousel';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import SanitizeHtml from '../../utils/SanitizeHtml.jsx';

// styles
import styles from './DesignsCarousel.scss';

type Props = {
    isMobile: boolean;
    iconicDesignsDirectoryPage: IconicDesignsDirectoryPage;
};

const DesignsCarouselComponent: FunctionComponent<Props> = ({
    isMobile,
    iconicDesignsDirectoryPage,
}) => {
    const { description, bannerImages = [] } = iconicDesignsDirectoryPage;

    return (
        <div
            className={classnames({
                [styles.container]: !isMobile,
                [styles.mobileContainer]: isMobile,
            })}
        >
            <div className={styles.images}>
                <Carousel
                    isAutoRun
                    isInfinite
                    hideArrows
                    slideCarouselItems
                    totalItems={bannerImages && bannerImages.length}
                    renderItem={({ index }: { index: number }) => {
                        const src = (bannerImages && bannerImages[index]?.imageUrl) || undefined;
                        return (
                            <img
                                data-tn="iconic-designs-img"
                                className={styles.image}
                                srcSet={getSrcSetString({ url: src })}
                                sizes="(max-width:768px) 100vw, (max-width:1024px) 50vw, 50vw"
                                src={getSrcString({
                                    url: src || '',
                                    width: isMobile
                                        ? MOBILE_IMAGE_DEFAULT_SIZE
                                        : RESP_IMAGE_DEFAULT_SIZE,
                                })}
                            />
                        );
                    }}
                />
            </div>
            <div className={styles.text}>
                <h1 className={styles.title} data-tn="iconic-designs-title">
                    <FormattedMessage
                        id="designs.designsCarousel.header"
                        defaultMessage="Iconic {break} Designs"
                        values={{
                            break: isMobile ? null : <br />,
                        }}
                    />
                </h1>
                <SanitizeHtml
                    className={styles.description}
                    content={description}
                    data-tn="designs-description"
                />
                {isMobile && (
                    <Link className={styles.viewAllDesigns} href="#all-designs">
                        <FormattedMessage
                            id="designs.designsCarousel.viewAllDesigns"
                            defaultMessage="View All Designs"
                        />
                    </Link>
                )}
            </div>
        </div>
    );
};

export const DesignsCarousel = createFragmentContainer(DesignsCarouselComponent, {
    iconicDesignsDirectoryPage: graphql`
        fragment DesignsCarousel_iconicDesignsDirectoryPage on IconicDesignsDirectoryPage {
            description
            bannerImages {
                imageUrl
            }
        }
    `,
});
